/**
 * Weeks, pure. A week starts on Monday and is named by that Monday as `YYYY-MM-DD`. The day a moment falls on is
 * read in a named time zone through `Intl.DateTimeFormat`; everything after that is calendar arithmetic in UTC, so
 * a daylight-saving change never makes a week six or eight days long and the browser's own zone is never asked.
 */
const DAY_MS = 86_400_000;
const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const formats = new Map<string, Intl.DateTimeFormat>();
/** The formatter for a zone, made once. Throws `RangeError` for a zone name `Intl` does not know. */
function formatFor(timeZone: string): Intl.DateTimeFormat {
  let f = formats.get(timeZone);
  if (!f) {
    f = new Intl.DateTimeFormat('en-US', { timeZone, calendar: 'gregory', numberingSystem: 'latn', year: 'numeric', month: '2-digit', day: '2-digit', weekday: 'short' });
    formats.set(timeZone, f);
  }
  return f;
}

/** `YYYY-MM-DD` → that day at midnight UTC, in milliseconds; NaN when it is not a day. */
function utcDay(day: string): number {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
  return m ? Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : NaN;
}
const iso = (ms: number): string => new Date(ms).toISOString().slice(0, 10);

/** The time zone to use: the one given when it is not blank and `Intl` knows it, else `UTC`. Never throws. */
export function zoneOrUtc(tz: string | null | undefined): string {
  const zone = (tz ?? '').trim();
  if (!zone) return 'UTC';
  try {
    formatFor(zone);
    return zone;
  } catch {
    return 'UTC';
  }
}

/** A day `n` days after (or, with a minus, before) `day`. Both are `YYYY-MM-DD`. */
export function addDays(day: string, n: number): string {
  const at = utcDay(day);
  if (!Number.isFinite(at) || !Number.isFinite(n)) throw new RangeError(`Not a day: ${day}`);
  return iso(at + Math.round(n) * DAY_MS);
}

/**
 * The Monday of the week a moment falls in, in the given time zone, as `YYYY-MM-DD`. Throws `RangeError` when the
 * moment cannot be read (or the zone is not one `Intl` knows — pass it through `zoneOrUtc` first).
 */
export function weekStart(instant: string | Date, timeZone: string): string {
  const at = instant instanceof Date ? instant : new Date(instant);
  if (!Number.isFinite(at.getTime())) throw new RangeError(`Not a moment in time: ${String(instant)}`);
  let year = NaN;
  let month = NaN;
  let day = NaN;
  let weekday = -1;
  for (const p of formatFor(timeZone).formatToParts(at)) {
    if (p.type === 'year') year = Number(p.value);
    else if (p.type === 'month') month = Number(p.value);
    else if (p.type === 'day') day = Number(p.value);
    else if (p.type === 'weekday') weekday = WEEKDAYS.indexOf(p.value);
  }
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day) || weekday < 0) throw new RangeError(`Not a moment in time: ${String(instant)}`);
  return iso(Date.UTC(year, month - 1, day - weekday));
}

/** Every Monday from `first` to `last`, both included, seven calendar days apart. Empty when `first` is after `last`. */
export function weeksBetween(first: string, last: string): string[] {
  const from = utcDay(first);
  const to = utcDay(last);
  const out: string[] = [];
  if (!Number.isFinite(from) || !Number.isFinite(to)) return out;
  for (let at = from; at <= to; at += 7 * DAY_MS) out.push(iso(at));
  return out;
}
